<template>
    <main class="main-content">
        <div class="content-card">
            <InfoPopup v-model="showInfoPopup" :title="infoTitle" :showCancelButton="showInfoPopupCancelButton">
                <p>{{ infoMessage }}</p>
            </InfoPopup>
            <div v-if='showHeader && !isAuthenticated' class="header">
                <div>
                    <h3>Explore Our Features Before You Onboard</h3>
                    <p>Create, customize, and deploy studio-quality voice
                        audio for any of your applications.</p>
                </div>
                <div class="register-actions">
                    <button class="btn-register" @click="handleRegister">Register now</button>
                </div>
            </div>
            <ul class="tabs">
                <li :class="{ active: activeTab === 'voiceGallery' }" @click="activeTab = 'voiceGallery'">Voice Gallery
                </li>
                <li :class="{ active: activeTab === 'voiceDesigner' }" @click="activeTab = 'voiceDesigner'">Voice
                    Designer
                </li>
                <li :class="{ active: activeTab === 'TTS' }" @click="activeTab = 'TTS'">Text to Speech
                </li>
            </ul>
            <div class="tab-content">
                <div v-if="activeTab === 'voiceGallery'" class="tab-voice-gallery">
                    <div class="voice-gallery-left">
                        <div class='voice-gallery-prompt-area'>
                            <textarea rows="4"
                                placeholder="A voice suitable for a podcast about scientific Arctic exploration-professional yet approachable, conveying curiosity, credibility, and a sense of discovery in extreme environments."></textarea>
                            <div class="prompt-actions">
                                <button class="btn btn-primary" @click="onVoiceGalleryClicked">
                                    <span>Recommend Voices</span>
                                </button>
                            </div>
                        </div>
                        <div class="voice-gallery-recommend-output">
                            <span class="voice-gallery-recommend-placeholder">This profile combines a professional tone
                                with an
                                approachable quality, suitable for conveying curiosity and credibility in a scientific
                                context.</span>
                        </div>
                        <div class="voice-gallery-section-header">
                            <div>
                                <h4>Find Your Perfect Voice</h4>
                                <p>Describe the voice you're looking for in detail, and our AI will find the best
                                    matches.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div class="voice-gallery-right">
                        <div class="voice-gallery-right-pm-list">
                            <div v-for="item in voiceDesignerVoices" :key="item.key"
                                class="voice-designer-pm-item vc-card" role="button" tabindex="0"
                                @click="onDemoVoiceClicked(item)">
                                <div class="vc-avatar">
                                    <img :src="item.voiceImageUrl" alt="Profile pic" />
                                </div>
                                <div class="vc-content">
                                    <div class="vc-name-desc">
                                        <div class="vc-name-tags">
                                            <h3>{{ item.voice_name }}</h3>
                                            <div class="vc-tags">
                                                <span v-if="item.accent">{{ item.accent }}</span>
                                                <span v-if="item.gender"> · {{ item.gender }}</span>
                                                <span v-if="item.ageLabel"> · {{ item.ageLabel }}</span>
                                            </div>
                                        </div>
                                        <p class="vc-description">{{ item.desc }}</p>
                                    </div>
                                    <div class="vc-middle">
                                        <div class="vc-progress-bar">
                                            <div class="vc-progress-bar-fill"
                                                :style="{ width: audioProgress[item.key] ? (audioProgress[item.key] * 100) + '%' : '0%' }">
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else-if="activeTab === 'voiceDesigner'" class="tab-voice-designer">
                    <div class="voice-designer-1-step">
                        <div class="step-container">
                            <div class="step-row">
                                <div class="step-record-text-info">
                                    <div class="step-record-text-info-header">
                                        Step 1 </div>
                                    <div class="step-record-text-info-desc">
                                        Choose a Base Voice or upload
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="voice-designer-pm-list">
                            <div v-for="item in voiceDesignerVoices" :key="item.key"
                                class="voice-designer-pm-item vc-card" role="button" tabindex="0"
                                @click="onVoiceDesignerClicked(item)">
                                <div class="vc-avatar">
                                    <img :src="item.voiceImageUrl" alt="Profile pic" />
                                </div>
                                <div class="vc-content">
                                    <div class="vc-name-desc">
                                        <div class="vc-name-tags">
                                            <h3>{{ item.voice_name }}</h3>
                                            <div class="vc-tags">
                                                <span v-if="item.accent">{{ item.accent }}</span>
                                                <span v-if="item.gender"> · {{ item.gender }}</span>
                                                <span v-if="item.ageLabel"> · {{ item.ageLabel }}</span>
                                            </div>
                                        </div>
                                        <p class="vc-description">{{ item.desc }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="voice-designer-2-step">
                        <div class="step-container">
                            <div class="step-row">
                                <div class="step-record-text-info">
                                    <div class="step-record-text-info-header">
                                        Step 2 </div>
                                    <div class="step-record-text-info-desc">
                                        Fine-Tune Your Voice with Prompt
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class='voice-designer-2-content'>
                            <textarea rows="4"
                                placeholder="A rich, warm, and authoritative narration voice, steady and clear, evoking wonder and respect. The tone is vivid, majestic, and immersive—capturing the energy and beauty of Africa's wildlife, while remaining calm and documentary-style."></textarea>
                            <div class="prompt-actions">
                                <button class="btn btn-primary" @click="onVoiceGalleryClicked">
                                    <span>Generate New Voice</span>
                                </button>
                            </div>
                        </div>

                        <div class="voice-designer-section-header">
                            <div>
                                <h4>Design Your Perfect Voice</h4>
                                <p>Create a new voice from a provided base voice or your own speech audio.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else-if="activeTab === 'TTS'" class="tab-tts">
                    <div class="tts-input-area">
                        <textarea rows="4"
                            placeholder="For aught that I could ever read, Could ever hear by tale or history, The course of true love never did run smooth."></textarea>
                        <div class="TTS-actions">
                            <div class="TTS-action-selections">
                                <DropdownList v-model="filters.model" label="Model" :items="optionModels" />
                                <DropdownList v-model="filters.voice" label="Voice" :items="optionVoices" />
                                <div class="btn-secondary" @click="onTTSClicked">
                                    <span>More Voices</span>
                                </div>
                            </div>
                            <button class="btn btn-primary" @click="onTTSClicked">
                                <span>Generate</span>
                            </button>
                        </div>
                    </div>
                    <div class="tts-section-header">
                        <div>
                            <h3>Generate Speech Using Voices</h3>
                            <p>Choose from a variety of voices to generate speech from your text input.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    </main>
</template>

<script>
import { inject, ref, reactive, watch, computed, onBeforeUnmount, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router';
import { requireAuth } from '@/api/auth'
import DropdownList from '@/components/common/DropdownList.vue';
import voiceDesignerVoices from '@/data/DemoVoices.js';
import InfoPopup from '@/components/common/InfoPopup.vue';
export default {
    name: 'DemoView',
    components: {
        DropdownList, InfoPopup
    },
    props: {
        showHeader: {
            type: Boolean,
            default: true
        },
        demoTarget: {
            type: String,
            default: "voiceGallery"
        }
    },
    setup(props) {
        const router = useRouter();
        const route = useRoute();

        const showInfoPopup = ref(false);
        const infoTitle = ref('');
        const infoMessage = ref('');
        const showInfoPopupCancelButton = ref(true);

        const activeTab = ref(route.query.demoTarget || props.demoTarget);
        watch(() => route.query.demoTarget, (val) => {
            if (val) activeTab.value = val;
        });

        const filters = reactive({ model: '', voice: '' });
        const MODEL_BUCKET = [
            { label: 'Pro', }, { label: 'Basic' }
        ];
        const optionModels = computed(() => MODEL_BUCKET.map(b => b.label));
        const optionVoices = computed(() => voiceDesignerVoices.map(b => b.label));

        const currentAudio = ref(null);
        const audioProgress = reactive({});
        const isAudioBusy = ref(false);

        const keycloak = inject('keycloak')

        const authToken = ref('')
        const isAuthenticated = computed(() => {
            return authToken.value && authToken.value !== ''
        })
        function handleRegister() {
            console.log('Prompting Keycloak register...')
            keycloak.register()
        }
        async function onDemoVoiceClicked(item) {
            if (item.label == 'More Voices') {
                onVoiceGalleryClicked();
                return;
            }
            if (isAudioBusy.value) {
                console.log('Audio is busy');
                return;
            }
            isAudioBusy.value = true;
            try {
                const key = item.key;
                if (currentAudio.value && currentAudio.value._itemKey === key) {
                    if (!currentAudio.value.paused) {
                        await currentAudio.value.pause();
                    } else {
                        await currentAudio.value.play();
                    }
                    return;
                }
                if (currentAudio.value) {
                    await currentAudio.value.pause();
                    currentAudio.value = null;
                    await new Promise(resolve => setTimeout(resolve, 50));
                }
                const audioUrl = item.voiceWavUrl;
                const audio = new Audio(audioUrl);
                audio._itemKey = key;
                currentAudio.value = audio;

                audioProgress[item.key] = 0;
                audio.ontimeupdate = () => {
                    audioProgress[item.key] = audio.currentTime / audio.duration;
                };
                audio.onended = () => {
                    audioProgress[item.key] = 0;
                    URL.revokeObjectURL(audioUrl);
                };
                try {
                    await audio.play();
                } catch (e) {
                    infoTitle.value = 'Error!';
                    infoMessage.value = "Play demo voice failed: " + e;
                    showInfoPopup.value = true;
                    showInfoPopupCancelButton.value = false;
                }
            }
            finally {
                isAudioBusy.value = false;
            }
        }

        async function onVoiceGalleryClicked() {
            if (!await requireAuth()) { return }
            router.push('/voice-gallery');
        }
        async function onVoiceDesignerClicked() {
            if (!await requireAuth()) { return }
            router.push('/voice-designer');
        }
        async function onTTSClicked() {
            if (!await requireAuth()) { return }
            router.push('/text-to-speech-projects');
        }
        watch(isAuthenticated, (val) => {
            if (val) {
                router.push('/');
            }
        });
        onMounted(() => {
            authToken.value = localStorage.getItem('auth_token') || ''
        })
        onBeforeUnmount(() => {
            if (currentAudio.value) {
                try {
                    currentAudio.value.pause();
                    currentAudio.value.currentTime = 0;
                } catch (e) {
                    infoTitle.value = 'Error!';
                    infoMessage.value = "Stop demo voice failed: " + e;
                    showInfoPopup.value = true;
                    showInfoPopupCancelButton.value = false;
                }
                currentAudio.value = null;
            }
        });
        return {
            props,
            isAuthenticated,
            activeTab,
            voiceDesignerVoices,
            filters,
            optionModels,
            optionVoices,
            isAudioBusy,
            currentAudio,
            audioProgress,
            showInfoPopup,
            infoTitle,
            infoMessage,
            showInfoPopupCancelButton,
            onDemoVoiceClicked,
            onVoiceGalleryClicked,
            onVoiceDesignerClicked,
            onTTSClicked,
            handleRegister,
        };
    }
};
</script>

<style scoped>
/* Content card - first major container */
.content-card {
    display: flex;
    flex-direction: column;
    background-color: var(--background-white);
    gap: 2rem;
    padding: 1.5rem;
    padding-left: 0;
    padding-right: 0;
}

/* Header section */
.header {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
}

.header h3 {
    margin: 0;
    font-weight: 600;
}

.btn-register {
    margin-top: 1rem;
    background: linear-gradient(90deg, #7924FF 0%, #45A2FF 100%);
    color: #ffffff;
    border: none;
    border-radius: 5px;
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
    cursor: pointer;
    transition: background 0.3s ease;
}

.btn-register:hover {
    background: linear-gradient(90deg, #45A2FF 0%, #7924FF 100%);
}

/* Navigation tabs */
.tabs {
    display: flex;
    flex-direction: row;
    justify-content: center;
    list-style: none;
}

.tabs li {
    padding: 0.5rem 1rem;
    cursor: pointer;
    margin-right: 1rem;
}

.tabs li.active {
    color: var(--purple);
    border-bottom: 1px solid var(--purple);
}


/* Voice Gallery Tab */
.tab-voice-gallery {
    display: flex;
    flex-direction: row;
    gap: 1rem;
    justify-content: center;
    align-items: stretch;
}

.voice-gallery-left {
    border-radius: 8px;
    padding: 1rem;
    background: linear-gradient(to bottom, #eddcfc 0%, #f2f7fc 100%);
    flex: 1 1 0;
    height: auto;
    display: flex;
    flex-direction: column;
}

.voice-gallery-prompt-area {
    background-color: white;
    border-radius: 8px;
    padding: 1rem;
    padding-top: 0;
}

.voice-gallery-left textarea {
    font-family: var(--app-font);
    width: 100%;
    font-size: 1rem;
    padding: 1rem;
    border: 1px solid transparent;
    border-radius: 8px;
    resize: none;
    min-height: 100px;
    margin-bottom: 1rem;
    margin-top: 1rem;
    transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.voice-gallery-left textarea:focus {
    outline: none;
    border-color: var(--purple);
    box-shadow: 0 0 0 3px rgba(74, 67, 227, 0.1);
}

.prompt-actions {
    text-align: right;
}

/* Button styles */
.btn {
    text-decoration: none;
    padding: 12px 24px;
    border-radius: 8px;
    font-weight: 500;
    transition: all 0.3s ease;
    display: inline-block;
    border: none;
    cursor: pointer;
}

.btn-primary {
    background-color: var(--purple);
    color: white;
    padding: 10px 20px;
}

.btn-primary:hover {
    background-color: var(--purple-hover);
}

.voice-gallery-recommend-output {
    margin-top: 1rem;
    padding: 1rem;
    background: #f8f9ff;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    font-size: 0.95rem;
    color: var(--text-primary);
    white-space: pre-line;
    min-height: 1.4em;
}

.voice-gallery-recommend-placeholder {
    color: var(--text-secondary);
    opacity: 0.7;
}

.voice-gallery-section-header {
    padding-top: 2rem;
}

.voice-gallery-section-header h4 {
    margin: 0;
    font-weight: 500;
    text-align: center;
}

.voice-gallery-section-header p {
    text-align: center;
}

.voice-gallery-right {
    flex: 1 1 0;
    height: auto;
    display: flex;
    flex-direction: column;
}

.voice-gallery-right-pm-list {
    flex: 1 1 0;
    overflow-y: auto;
    display: grid;
    grid-auto-rows: 120px;
    align-content: start;
    height: 100%;
    scrollbar-width: none;
    -ms-overflow-style: none;
    box-shadow: 0 8px 16px -8px rgba(0, 0, 0, 0.08);
}

.voice-gallery-right-pm-list::-webkit-scrollbar {
    display: none;
}

/* Voice cards - common styles */
.voice-designer-pm-item {
    padding: .6rem 1rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
    cursor: pointer;
    transition: background .15s;
    overflow: hidden;
}

.vc-card {
    transition: all 0.3s ease;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: .3rem;
    padding: 0.6rem 0.6rem 0.6rem 1.2rem;
    height: 100%;
    overflow: hidden;
}

.vc-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
    border: white;
}

.vc-avatar {
    width: 64px;
    height: 64px;
    border-radius: 16px;
    margin-right: 12px;
    overflow: hidden;
    flex-shrink: 0;
    background: transparent;
    position: relative;
    cursor: pointer;
    transition: transform 0.2s, opacity 0.2s;
}

.vc-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.2s;
}

.vc-avatar.disabled {
    pointer-events: none;
    opacity: 0.6;
}

.vc-avatar:hover:not(.disabled) img {
    transform: scale(1.05);
}

.vc-content {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    gap: 1.5rem;
    width: 100%;
    height: 100%;
}

.vc-name-desc {
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex: 2 1 0;
    min-width: 0;
}

.vc-name-tags h3 {
    font-size: 1.1rem;
}

.vc-tags {
    font-size: 0.8rem;
    color: var(--purple);
    font-weight: 500;
}

.vc-description {
    font-size: 0.8rem;
    color: var(--text-secondary);
    margin-bottom: 0.5rem;
    line-height: 1.3;
}

.vc-middle {
    width: 200px;
    min-width: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    margin: 0 0.5rem;
}

.vc-progress-bar {
    width: 180px;
    height: 6px;
    background: #eee;
    border-radius: 3px;
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
}

.vc-progress-bar-fill {
    height: 100%;
    background: var(--purple);
    border-radius: 3px;
    position: absolute;
    left: 0;
    top: 0;
    transition: width 0.1s linear;
    width: 0%;
}

/* Voice Designer Tab */
.tab-voice-designer {
    display: flex;
    flex-direction: row;
    gap: 1rem;
    justify-content: center;
    align-items: stretch;
}

.voice-designer-1-step {
    flex: 1 1 0;
    height: auto;
    display: flex;
    flex-direction: column;
    padding: 1rem;
    padding-bottom: 0;
}

.step-row {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
}

.step-record-text-info {
    box-sizing: content-box;
    color: #000000CC;
    letter-spacing: 0px;
    text-align: left;
}

.step-record-text-info-header {
    height: 40px;
    font-weight: 700;
    font-size: 32px;
    line-height: 40px;
}

.step-record-text-info-desc {
    height: 28px;
    font-weight: 500;
    font-size: 1rem;
    line-height: 28px;
}

.voice-designer-pm-list {
    flex: 1 1 0;
    overflow-y: auto;
    display: grid;
    grid-auto-rows: 120px;
    align-content: start;
    height: 100%;
    scrollbar-width: none;
    -ms-overflow-style: none;
    box-shadow: 0 8px 16px -8px rgba(0, 0, 0, 0.08);
}

.voice-designer-pm-list::-webkit-scrollbar {
    display: none;
}

.voice-designer-2-step {
    flex: 1 1 0;
    height: auto;
    display: flex;
    flex-direction: column;
    padding: 1rem;
    border-radius: 8px;
    background: linear-gradient(to bottom, #eddcfc 0%, #f2f7fc 100%);
}

.voice-designer-2-content {
    padding: 1rem;
    padding-top: 0;
    margin-top: 1rem;
    background: white;
    border-radius: 8px;
}

.voice-designer-2-content textarea {
    font-family: var(--app-font);
    width: 100%;
    font-size: 1rem;
    padding: 1rem;
    border: 1px solid transparent;
    border-radius: 8px;
    resize: none;
    min-height: 100px;
    margin-bottom: 1rem;
    margin-top: 1rem;
    transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.voice-designer-2-content textarea:focus {
    outline: none;
    border-color: var(--purple);
    box-shadow: 0 0 0 3px rgba(74, 67, 227, 0.1);
}

.voice-designer-section-header {
    padding-top: 2rem;
}

.voice-designer-section-header h4 {
    margin: 0;
    font-weight: 500;
    text-align: center;
}

.voice-designer-section-header p {
    text-align: center;
}

/* TTS Tab */
.tab-tts {
    width: 80%;
    margin-left: auto;
    margin-right: auto;
    padding: 1rem;
    border-radius: 8px;
    background: linear-gradient(to bottom, #eddcfc 0%, #f2f7fc 100%);
}

.tts-input-area {
    border-radius: 8px;
    background: white;
    padding: 1rem;
    padding-top: 0;
}

.tab-tts textarea {
    font-family: var(--app-font);
    width: 100%;
    font-size: 1rem;
    padding: 1rem;
    border: 1px solid transparent;
    border-radius: 8px;
    resize: none;
    min-height: 100px;
    margin-bottom: 1rem;
    margin-top: 1rem;
    transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.tab-tts textarea:focus {
    outline: none;
    border-color: var(--purple);
    box-shadow: 0 0 0 3px rgba(74, 67, 227, 0.1);
}

.TTS-actions {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
}

.TTS-action-selections {
    display: flex;
    gap: 1rem;
    flex-direction: row;
}

.btn-secondary {
    margin-left: 1rem;
    background-color: var(--background-white);
    border: none;
    cursor: pointer;
    padding-top: 0;
    padding-bottom: 0;
    padding-left: 1rem;
    padding-right: 1rem;
    border-radius: 10px;
    color: var(--purple);
}

.btn-secondary:hover {
    background-color: var(--purple-ultra-light);
    color: var(--purple-hover);
}

.tts-section-header h3 {
    margin: 0;
    margin-top: 1rem;
    font-weight: 400;
    text-align: center;
}

.tts-section-header p {
    text-align: center;
}
</style>